import { useState, useRef, useEffect } from "react";
import { Controller, Control, FieldValues, Path } from "react-hook-form";

type MultiSelectProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  options: { value: string; text: string }[];
  disabled?: boolean;
  placeholder?: string;
};

function MultiSelect<T extends FieldValues>({
  control,
  name,
  options,
  placeholder,
  disabled = false,
}: MultiSelectProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      disabled={disabled}
      render={({ field, fieldState }) => (
        <MultiSelectComponent
          value={field.value || []} // Default to empty array if no value
          onChange={field.onChange}
          options={options}
          placeholder={placeholder ?? ""}
          error={fieldState.error?.message} // Pass validation error
          disabled={disabled}
        />
      )}
    />
  );
}

type Option = {
  value: string;
  text: string;
};

type MultiSelectComponentProps = {
  value: string[];
  onChange: (value: string[]) => void;
  options: Option[];
  placeholder: string;
  disabled?: boolean;
  error?: string;
};

function MultiSelectComponent({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  error,
}: MultiSelectComponentProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as HTMLDivElement)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Toggle an option (select or deselect)
  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  // Remove an option from the selected list
  const removeOption = (optionValue: string) => {
    onChange(value.filter((v) => v !== optionValue));
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Input area showing selected tags */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          border: "1px solid #ccc",
          padding: "5px",
          cursor: !disabled ? "pointer" : "default",
          minHeight: "30px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          borderRadius: "4px",
        }}
      >
        {value.length > 0 ? (
          value.map((val) => {
            const option = options.find((o) => o.value === val);
            return (
              <span
                key={val}
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "2px 6px",
                  margin: "2px",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "14px",
                }}
              >
                {option?.text || val}
                {!disabled && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent dropdown toggle
                      !disabled && removeOption(val);
                    }}
                    type="button"
                    style={{
                      marginLeft: "4px",
                      border: "none",
                      background: "none",
                      cursor: !disabled ? "pointer" : "default",
                      fontSize: "16px",
                      lineHeight: "1",
                    }}
                  >
                    &times;
                  </button>
                )}
              </span>
            );
          })
        ) : (
          <span style={{ color: "#888", fontSize: "14px" }}>{placeholder}</span>
        )}
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 10,
          }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => toggleOption(option.value)}
              style={{
                padding: "8px",
                cursor: "pointer",
                backgroundColor: value.includes(option.value)
                  ? "#e0e0e0"
                  : "#fff",
                // ':hover': { backgroundColor: '#f5f5f5' }, // Note: Inline styles don’t support :hover, use CSS for this in practice
              }}
            >
              {option.text}
            </div>
          ))}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
